<script setup lang="ts">
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import PlanCard from '@/components/Plan/PlanCard.vue';
import { useExternalUrl } from '@/composables/useExternalUrl';
import { useFetch } from '@/composables/useFetch';
import { useInAppPurchase } from '@/composables/useInAppPurchase';
import { usePlatform } from '@/composables/usePlatform';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { SubscriptionPlan } from '@/types/api';
import type { Invoice, UpcomingInvoice } from '@/types/stripe'; // Added UpcomingInvoice
import { formatDate } from '@/utils/date';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const envStore = useEnvStore();
const { fetchData } = useFetch();
const { t } = useI18n();
const router = useRouter();
const toast = useToast();
const userStore = useUserStore();
const { navigateToExternalUrl } = useExternalUrl();
const { isNative, isIOS } = usePlatform();
const { restorePurchases, isProcessing: isRestoringPurchases, requestRefund, openManageSubscriptions } = useInAppPurchase();

const isRequestingRefund = ref(false);

const showRestoreSection = ref(false);

const billingEmail = ref('');
const billingUser = ref<{ id: string; name: string | null; email: string } | null>(null);
const plan = ref<SubscriptionPlan>();
const paymentMethod = ref<unknown>(null);
const invoices = ref<Invoice[]>([]);
const loading = ref(false);
const upcomingInvoice = ref<UpcomingInvoice | null>(null); // Added upcomingInvoice ref
const monthlyCreditsRemaining = ref<number>(0);
const uniqueCreditsRemaining = ref<number>(0);
const totalCredits = ref<number>(0);

// Enhanced subscription details
const subscriptionStatus = ref<string>('');
const isTrial = ref<boolean>(false);
const trialEndsAt = ref<string | null>(null);
const isCancelled = ref<boolean>(false);
const isOnGracePeriod = ref<boolean>(false);
const subscriptionEndsAt = ref<string | null>(null);
const autoRenews = ref<boolean>(true);
const currentPeriodStartAt = ref<string>('');
const currentPeriodEndsAt = ref<string>('');
const billingPeriod = ref<string>('yearly');
const platform = ref<string | null>(null);
const paymentDetails = ref<{
  last_invoice_amount_paid: number | null;
  currency: string;
  discount_applied: {
    coupon_id: string;
    name: string;
    percent_off: number | null;
    amount_off: number | null;
    currency: string | null;
    duration: string;
    duration_in_months: number | null;
  } | null;
} | null>(null);

// Special status states
const isTrialEligible = ref<boolean>(false);
const isLapsed = ref<boolean>(false);
// const statusMessage = ref<string>('');
const isRedirectingToPortal = ref(false);

// Apple offer code details
const offerType = ref<string | null>(null);
const offerIdentifier = ref<string | null>(null);
const offerDiscountType = ref<string | null>(null);
const platformSubscriptionId = ref<string | null>(null);

const redirectToBillingPortal = async () => {
  isRedirectingToPortal.value = true;
  try {
    // This endpoint should return a { url: '...' } object from your backend
    const { data, error } = await fetchData(`${envStore.apiUrl}/billing`, 'GET');
    console.log(data);
    if (error || !data) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.billingPortalError'),
        life: 3000,
      });
    } else {
      if (isNative.value) {
        await navigateToExternalUrl(data.url, { target: '_blank' });
      } else {
        await navigateToExternalUrl(data.url);
      }
    }
  } catch (e) {
    console.error('Error redirecting to billing portal:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('subscription.billingPortalError'),
      life: 3000,
    });
  } finally {
    isRedirectingToPortal.value = false;
  }
};

const fetchSubscriptionDetails = async () => {
  loading.value = true;
  const { data } = await fetchData(`${envStore.apiUrl}/subscription-details`);
  if (data) {
    // Check for special statuses first
    // if (data.status === 'ELIGIBLE_FOR_INITIAL_TRIAL') {
    //   plan.value = data.trial_plan_details;
    //   isTrialEligible.value = true;
    //   statusMessage.value = data.message;
    //   loading.value = false;
    //   return;
    // }

    // if (data.status === 'LAPSED_NO_ACTIVE_SUBSCRIPTION') {
    //   plan.value = data.freemium_plan_details;
    //   isLapsed.value = true;
    //   statusMessage.value = data.message;
    //   loading.value = false;
    //   return;
    // }

    billingEmail.value = data.billing_email;
    billingUser.value = data.billing_user;
    plan.value = data.plan;
    paymentMethod.value = data.payment_method;
    invoices.value = data.invoices;
    upcomingInvoice.value = data.upcoming_invoice; // Assign upcoming_invoice

    // Enhanced subscription details
    subscriptionStatus.value = data.status;
    isTrial.value = data.is_trial;
    trialEndsAt.value = data.trial_ends_at;
    isCancelled.value = data.is_cancelled;
    isOnGracePeriod.value = data.is_on_grace_period;
    subscriptionEndsAt.value = data.subscription_ends_at;
    autoRenews.value = data.auto_renews;
    currentPeriodStartAt.value = data.current_period_start_at;
    currentPeriodEndsAt.value = data.current_period_ends_at;
    billingPeriod.value = data.billing_period || data.plan?.billing_period || 'yearly';
    platform.value = data.platform;
    paymentDetails.value = data.payment_details;

    // Add these lines
    monthlyCreditsRemaining.value = data.monthly_credits_remaining || 0;
    uniqueCreditsRemaining.value = data.unique_credits_remaining || 0;
    totalCredits.value = data.total_credits || 0;

    // Apple offer code details
    offerType.value = data.offer_type || null;
    offerIdentifier.value = data.offer_identifier || null;
    offerDiscountType.value = data.offer_discount_type || null;
    platformSubscriptionId.value = data.platform_subscription_id || null;
  }
  loading.value = false;
};

const handleRequestRefund = async () => {
  if (!platformSubscriptionId.value) {
    toast.add({
      severity: 'warn',
      summary: t('common.error'),
      detail: t('subscription.iap.refundNoTransaction'),
      life: 5000,
    });
    return;
  }
  isRequestingRefund.value = true;
  try {
    const result = await requestRefund(platformSubscriptionId.value);
    if (result === 'success') {
      toast.add({
        severity: 'info',
        summary: t('subscription.iap.refundRequested'),
        detail: t('subscription.iap.refundRequestedDetail'),
        life: 5000,
      });
    }
  } finally {
    isRequestingRefund.value = false;
  }
};

const dynamicWarningThreshold = computed(() => {
  const monthlyCreditsAllowed = plan.value?.features?.ai_generations_per_month || 0;
  return Math.ceil(monthlyCreditsAllowed * 0.1);
});

fetchSubscriptionDetails();
</script>

<template>
  <div v-if="loading" class="mx-auto mt-6 flex justify-center">
    <BaseLoader />
  </div>
  <div v-else class="mx-auto flex max-w-2xl flex-col gap-6">
    <!-- iOS Message with Magic Link -->
    <!-- <div v-if="isNative" class="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center">
      <i class="pi pi-info-circle mb-3 text-4xl text-blue-600"></i>
      <h3 class="mb-2 font-semibold text-blue-900">{{ t('subscription.iosManageTitle') }}</h3>
      <p class="mb-4 text-blue-800">{{ t('subscription.iosManageMessage') }}</p>
      <Button
        :label="t('subscription.magicLink.generate')"
        icon="pi pi-link"
        severity="info"
        :loading="isGeneratingMagicLink"
        @click="() => generateAndOpenMagicLink('/profile/subscriptions')"
      />
    </div> -->

    <!-- Non-iOS Subscription Information -->
    <div class="mb-2">
      <h3 class="mb-2 font-semibold">{{ t('subscription.infoTitle') }}</h3>

      <!-- iOS Restore Purchases - Subtle expandable section -->
      <div v-if="isNative && isIOS" class="mb-4">
        <button
          class="flex w-full items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          @click="showRestoreSection = !showRestoreSection"
        >
          <i class="pi pi-question-circle"></i>
          <span>{{ t('subscription.iap.purchaseNotShowing') }}</span>
          <i :class="showRestoreSection ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs"></i>
        </button>

        <Transition name="slide-fade">
          <div v-if="showRestoreSection" class="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
            <p class="mb-3 text-sm text-gray-600">{{ t('subscription.iap.restoreDescription') }}</p>
            <Button
              :label="t('subscription.iap.syncPurchases')"
              icon="pi pi-sync"
              :loading="isRestoringPurchases"
              outlined
              size="small"
              severity="secondary"
              @click="restorePurchases"
            />
          </div>
        </Transition>
      </div>

      <div v-if="userStore.user?.active_restaurant?.subscription" class="mb-2 flex w-full flex-col gap-3 text-center">
        <!-- Show Stripe billing portal for Stripe subscriptions only -->
        <div v-if="platform === 'stripe'" class="flex justify-center">
          <Button
            :label="t('subscription.manageBilling')"
            icon="pi pi-external-link"
            :loading="isRedirectingToPortal"
            outlined
            size="small"
            @click="redirectToBillingPortal"
          />
        </div>
        <!-- For Apple subscriptions, guide user to App Store with detailed info -->
        <div v-else-if="platform === 'apple'" class="rounded-lg border border-blue-200 bg-blue-50 p-4 text-left">
          <div class="mb-2 flex items-center gap-2">
            <i class="pi pi-apple text-lg text-blue-600"></i>
            <span class="font-semibold text-blue-900">{{ t('subscription.manageApple') }}</span>
          </div>
          <p class="mb-3 text-sm text-blue-800">{{ t('subscription.iap.manageAppleSubscription') }}</p>
          <div class="flex flex-col gap-2">
            <Button
              :label="t('subscription.iap.openAppStoreSettings')"
              icon="pi pi-external-link"
              size="small"
              severity="info"
              outlined
              @click="openManageSubscriptions()"
            />
            <Button
              :label="t('subscription.iap.requestRefund')"
              icon="pi pi-undo"
              size="small"
              severity="secondary"
              text
              :loading="isRequestingRefund"
              @click="handleRequestRefund"
            />
          </div>
        </div>
        <!-- For Google subscriptions, guide user to Play Store -->
        <div v-else-if="platform === 'google'" class="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <i class="pi pi-android mr-2"></i>
          {{ t('subscription.manageGoogle') }}
        </div>
      </div>

      <!-- Special Status Banner for Trial Eligible -->
      <!-- <div v-if="isTrialEligible" class="mb-4 rounded-lg bg-green-50 border border-green-200 p-4">
      <div class="flex items-center">
        <i class="pi pi-gift text-green-600 mr-2"></i>
        <div>fdsafds
          <h4 class="font-semibold text-green-800">{{ t('subscription.trialEligibleTitle') }}</h4>
          <p class="text-green-700 text-sm">{{ statusMessage }}</p>
        </div>
      </div>
    </div> -->

      <!-- Special Status Banner for Lapsed Subscription -->
      <!-- <div v-if="isLapsed" class="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 p-4">
      <div class="flex items-center">
        <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
        <div>
          <h4 class="font-semibold text-yellow-800">{{ t('subscription.subscriptionLapsed') }}</h4>
          <p class="text-yellow-700 text-sm">{{ statusMessage }}</p>
        </div>
      </div>
    </div> -->

      <!-- Credit Balance Display -->
      <div v-if="totalCredits === 0">
        <div class="mb-2 rounded-lg border border-red-200 bg-red-50 p-4 shadow">
          <div class="mb-3 flex items-center">
            <i class="pi pi-times-circle mr-2 text-red-600"></i>
            <h4 class="font-semibold text-red-800">{{ t('credits.noMoreCredit.title') }}</h4>
          </div>
          <p class="mb-4 text-sm text-red-700">{{ t('credits.noMoreCredit.message') }}</p>
          <Button
            :label="t('credits.buyMore')"
            icon="pi pi-plus"
            outlined
            size="small"
            class="mt-4 w-full"
            @click="router.push({ path: '/subscription/credits/purchase' })"
          />
        </div>
      </div>
      <div v-else-if="totalCredits <= dynamicWarningThreshold" class="mb-2 rounded-lg border border-orange-200 bg-orange-50 p-4 shadow">
        <div class="mb-3 flex items-center">
          <i class="pi pi-exclamation-triangle mr-2 text-orange-600"></i>
          <h4 class="font-semibold text-orange-800">{{ t('credits.warning.title') }}</h4>
        </div>
        <p class="mb-4 text-sm text-orange-700">{{ t('credits.warning.message', { credits: totalCredits }) }}</p>
        <Button
          :label="t('credits.buyMore')"
          icon="pi pi-plus"
          outlined
          size="small"
          class="mt-4 w-full"
          @click="router.push({ path: '/subscription/credits/purchase' })"
        />
      </div>

      <!-- Trial Status Banner -->
      <div v-if="isTrial && plan" class="mb-2 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div class="flex items-center">
          <i class="pi pi-info-circle mr-2 text-blue-600"></i>
          <div>
            <h4 class="font-semibold text-blue-800">{{ t('subscription.trialActive') }}</h4>
            <p class="text-sm text-blue-700">{{ t('subscription.trialEndsOn') }} {{ formatDate(new Date(trialEndsAt!)) }}</p>
          </div>
        </div>
      </div>

      <!-- Cancellation/Grace Period Banner -->
      <div v-if="isCancelled || isOnGracePeriod" class="mb-2 rounded-lg border border-orange-200 bg-orange-50 p-2">
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle mr-2 text-orange-600"></i>
          <div>
            <h4 class="font-semibold text-orange-800">
              {{ isCancelled ? t('subscription.cancelled') : t('subscription.gracePeriod') }}
            </h4>
            <p v-if="subscriptionEndsAt" class="text-sm text-orange-700">
              {{ t('subscription.endsOn') }} {{ formatDate(new Date(subscriptionEndsAt)) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Apple Offer Code Active Banner -->
      <div v-if="offerType === 'offer_code' && platform === 'apple'" class="mb-2 rounded-lg border border-green-200 bg-green-50 p-4">
        <div class="flex items-center">
          <i class="pi pi-tag mr-2 text-green-600"></i>
          <div>
            <h4 class="font-semibold text-green-800">{{ t('subscription.offerCode.active') }}</h4>
            <p v-if="offerIdentifier" class="text-sm text-green-700">
              {{ t('subscription.offerCode.identifier', { code: offerIdentifier }) }}
            </p>
            <p v-if="offerDiscountType" class="text-sm text-green-700">
              {{ t(`subscription.offerCode.discountType.${offerDiscountType}`) }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="plan" class="my-2 flex flex-col items-center gap-8">
        <PlanCard :plan="plan" show-features hide-price />

        <Button
          :label="!userStore.user?.active_restaurant?.subscription ? t('subscription.chooseSubscription') : t('subscription.seeSubscriptions')"
          icon="pi pi-arrow-right"
          outlined
          @click="router.push({ path: '/subscription/plans' })"
        ></Button>

        <!-- Payment Information (only show if not special status) -->
        <div v-if="!isTrialEligible && !isLapsed && false" class="w-full max-w-md rounded bg-white p-4 shadow">
          <h4 class="mb-3 font-semibold">{{ t('subscription.paymentInfo') }}</h4>

          <!-- Current Payment Amount -->
          <div v-if="paymentDetails?.last_invoice_amount_paid !== null" class="mb-3">
            <p class="text-sm text-gray-600">{{ t('subscription.lastPayment') }}</p>
            <p class="font-medium">{{ paymentDetails?.last_invoice_amount_paid }} {{ paymentDetails?.currency?.toUpperCase() }}</p>
          </div>

          <!-- Discount Applied -->
          <div v-if="paymentDetails?.discount_applied && !isTrial" class="rounded border border-green-200 bg-green-50 p-3">
            <div class="mb-1 flex items-center">
              <i class="pi pi-tag mr-2 text-green-600"></i>
              <span class="font-medium text-green-800">{{ t('subscription.discountApplied') }}</span>
            </div>
            <p class="text-sm text-green-700">{{ paymentDetails?.discount_applied?.name }}</p>
            <p class="text-sm text-green-700">
              <span v-if="paymentDetails?.discount_applied?.percent_off"> -{{ paymentDetails?.discount_applied?.percent_off }}% </span>
              <span v-else-if="paymentDetails?.discount_applied?.amount_off">
                -{{ paymentDetails?.discount_applied?.amount_off }} {{ paymentDetails?.discount_applied?.currency?.toUpperCase() }}
              </span>
            </p>
          </div>

          <!-- Renewal Information -->
          <div class="text-sm text-gray-600">
            <p v-if="autoRenews && !isCancelled && !isOnGracePeriod && !upcomingInvoice">
              {{ t('subscription.renewsOn') }} {{ formatDate(new Date(currentPeriodEndsAt)) }}
            </p>
            <p v-else-if="!autoRenews">
              {{ t('subscription.willNotRenew') }}
            </p>
          </div>

          <!-- Upcoming Invoice Details -->
          <div v-if="upcomingInvoice && autoRenews && !isCancelled && !isOnGracePeriod && false" class="mt-4 border-t border-gray-200 pt-4">
            <h5 class="mb-2 font-semibold text-gray-700">{{ t('subscription.upcomingInvoiceTitle') }}</h5>
            <div class="space-y-1 text-sm text-gray-600">
              <p>
                {{ t('subscription.nextPaymentOn') }}:
                <span class="font-medium text-gray-800">{{ formatDate(new Date(upcomingInvoice?.next_payment_attempt!)) }}</span>
              </p>
              <p>
                {{ t('subscription.amountDue') }}:
                <span class="font-medium text-gray-800">{{ upcomingInvoice?.total }} {{ upcomingInvoice?.currency.toUpperCase() }}</span>
              </p>
              <p v-if="(upcomingInvoice?.discount_amount ?? 0) > 0">
                {{ t('subscription.discountIncluded') }}:
                <span class="font-medium text-green-600">-{{ upcomingInvoice?.discount_amount }} {{ upcomingInvoice?.currency.toUpperCase() }}</span>
              </p>
              <p class="text-xs">
                ({{ t('subscription.periodCovered') }}: {{ formatDate(new Date(upcomingInvoice?.period_start!)) }} -
                {{ formatDate(new Date(upcomingInvoice?.period_end!)) }})
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mx-auto flex max-w-2xl flex-col gap-6">
        <p>{{ t('subscription.noActive') }}</p>
        <Button
          :label="t('subscription.otherPlans')"
          icon="pi pi-arrow-right"
          outlined
          @click="router.push({ path: '/subscription/plans' })"
        ></Button>
      </div>

      <!-- Billing Information (only show if we have billing data) -->
      <div v-if="!isTrialEligible && !isLapsed && false">
        <h2 class="mb-2 font-bold text-xl">{{ t('subscription.billingInfoTitle') }}</h2>
        <div class="mb-4 rounded bg-white p-4 shadow">
          <h3 class="mb-2 font-semibold">{{ t('subscription.billingContactTitle') }}</h3>
          <div v-if="billingUser">
            <p>
              <span class="font-medium">{{ t('subscription.billingEmail') }} : {{ billingEmail }}</span>
            </p>
            <p v-if="billingUser?.name">
              <span class="font-medium">{{ t('subscription.billingName') }} : {{ billingUser?.name }}</span>
            </p>
          </div>
          <div v-else>
            <p>{{ t('subscription.noBillingContact') }}</p>
          </div>
        </div>

        <!-- Invoices Section -->
        <div v-if="false" class="rounded bg-white p-4 shadow">
          <h3 class="mb-2 font-semibold">{{ t('subscription.invoicesTitle') }}</h3>
          <div v-if="invoices.length">
            <ul class="divide-y">
              <li v-for="invoice in invoices" :key="invoice.id" class="flex items-center justify-between py-2">
                <div class="flex flex-col">
                  <span class="font-medium">{{ formatDate(new Date(invoice.date)) }}</span>
                  <span class="text-xs text-gray-600"
                    >{{ invoice.amount_paid }} {{ invoice.currency?.toUpperCase() }} | {{ t(`subscription.invoiceStatus.${invoice.status}`) }}</span
                  >
                </div>
                <a :href="invoice.download_url" target="_blank" class="ml-2 text-blue-600 hover:underline">
                  {{ t('subscription.invoiceDownload') }}
                </a>
              </li>
            </ul>
          </div>
          <div v-else>
            <p>{{ t('subscription.noInvoices') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
